import React from 'react';
import ReportCard from '../components/ReportCard';
import StackedShareBar from '../components/StackedShareBar';
import SegmentLegend from '../components/SegmentLegend';
import { MIN_COHORT, SMALL_SAMPLE_NOTE } from '../copy';
import { collapseBuckets, fmtCount, fmtDecimal, fmtPct, share, tip } from '../format';

const LABELS = ['Zero', 'One', 'Two or more'];

const GenderRow = ({ entry }) => {
  const total = entry.total;
  const buckets = collapseBuckets(entry.buckets, 2);
  const shares = buckets.map((bucket) => share(bucket.count, total));

  const segments = buckets.map((bucket, index) => ({
    key: bucket.flags,
    length: shares[index],
    tip: tip(
      `${entry.gender} · ${LABELS[index]}`,
      `${fmtPct(shares[index])} — ${fmtCount(bucket.count)} of ${fmtCount(total)}`
    ),
  }));

  return (
    <div>
      <div className='mb-ds-2 flex items-baseline justify-between'>
        <span className='text-[14px] font-semibold text-gray-900'>{entry.gender}</span>
        <span className='font-jetbrains-mono text-[12px] text-gray-600'>
          {`avg burden ${fmtDecimal(entry.avg_burden)}`}
        </span>
      </div>

      {total >= MIN_COHORT ? (
        <>
          <StackedShareBar segments={segments} height={34} radius='rounded-[8px]' />
          {/* The percentage strip is width-matched to the segments above it, so each figure sits under
              its own band rather than in an evenly spaced row. */}
          <div className='mt-ds-2 flex font-jetbrains-mono text-[12px] text-gray-700'>
            {shares.map((value, index) => (
              <span
                key={index}
                style={{ width: `${value}%` }}
                className={index === shares.length - 1 ? 'font-semibold text-ds-amber-dk' : ''}
              >
                {fmtPct(value)}
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className='m-0 text-ts-md text-gray-600'>{SMALL_SAMPLE_NOTE}</p>
      )}
    </div>
  );
};

/**
 * The compounded load per gender, with the same figures repeated as a plain table beside it — the chart
 * shows the shape, the table lets the reader quote a number.
 */
const DeficiencyBurden = ({ data }) => {
  const male = data.by_gender.find((entry) => entry.gender === 'Male') || {};
  const female = data.by_gender.find((entry) => entry.gender === 'Female') || {};
  const twoPlus = (entry) => share(collapseBuckets(entry.buckets, 2)[2].count, entry.total);
  const clean = (entry) => share(collapseBuckets(entry.buckets, 2)[0].count, entry.total);
  const heavier = (male.avg_burden || 0) >= (female.avg_burden || 0) ? 'Men' : 'Women';

  const rows = [
    ['Male at 2+', fmtPct(twoPlus(male))],
    ['Female at 2+', fmtPct(twoPlus(female))],
    ['Male average burden', fmtDecimal(male.avg_burden)],
    ['Female average burden', fmtDecimal(female.avg_burden)],
    ['Male “clean” rate', fmtPct(clean(male))],
    ['Female “clean” rate', fmtPct(clean(female))],
  ];

  return (
    <div className='mt-ds-9 grid grid-cols-1 items-start gap-ds-5 lg:grid-cols-[1.5fr_1fr]'>
      <ReportCard fileName='compounded-load-by-gender' className='p-[26px]'>
        <h3 className='m-0 font-fig-extrabold text-[26px] font-bold leading-[1.18] text-gray-900'>
          {`${heavier} carry a heavier compounded load`}
        </h3>
        <p className='mb-[22px] mt-[6px] text-xs text-gray-600'>
          Share of each gender falling into zero / one / two-or-more abnormality bands
        </p>
        <SegmentLegend labels={LABELS} gap={20} />
        <div className='grid gap-[18px]'>
          {data.by_gender.map((entry) => (
            <GenderRow key={entry.gender} entry={entry} />
          ))}
        </div>
      </ReportCard>

      <ReportCard fileName='reading-the-split' className='p-[26px]'>
        <div className='mb-[18px] text-ts-xs font-semibold uppercase tracking-[0.12em] text-ds-brand'>
          Reading the split
        </div>
        <div className='grid'>
          {rows.map(([label, value], index) => (
            <div
              key={label}
              className={`flex items-baseline justify-between py-ds-3 ${
                index === rows.length - 1 ? '' : 'border-b border-gray-300'
              }`}
            >
              <span className='text-[14px] text-gray-700'>{label}</span>
              <span className='font-jetbrains-mono text-[17px] font-semibold text-gray-900'>{value}</span>
            </div>
          ))}
        </div>
      </ReportCard>
    </div>
  );
};

export default DeficiencyBurden;
